import type { DataModelState, ProgramMappingDto, ProgramMappingStateDto, SoftwareDto } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Badge, Button, ErrorText, Select } from '../../ui';

/**
 * Che cosa diventano le righe di ogni lettura del programma (ADR 0168): la voce di un tipo del modello e le cose che
 * nomina (fornitori, clienti…). La propone il motore, la persona la corregge: una mappatura sua il motore non la rifà.
 */
export function ProgramMappings({ s }: { s: SoftwareDto }) {
  const { tenant, t } = useTenant();
  const queryClient = useQueryClient();
  const path = `/data-model/programs/${s.key}/mappings`;
  const q = useTenantQuery<ProgramMappingStateDto[]>(['data-model', 'programs', s.key], path);
  const model = useTenantQuery<DataModelState>(['data-model'], '/data-model');
  const update = (list: ProgramMappingStateDto[]) => {
    queryClient.setQueryData(['tenant', tenant.id, 'data-model', 'programs', s.key], list);
    void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'data-model'] });
  };
  const save = useMutation({
    mutationFn: (v: { lettura: string; mappatura: ProgramMappingDto }) => api<ProgramMappingStateDto[]>(t(`${path}/${v.lettura}`), { body: v.mappatura }),
    onSuccess: update,
  });
  const redo = useMutation({ mutationFn: (lettura: string) => api<ProgramMappingStateDto[]>(t(`${path}/${lettura}/redo`), { body: {} }), onSuccess: update });
  const types = (model.data?.data ?? []).filter((x) => x.stato === 'attivo');
  const label = (key: string | null) => types.find((x) => x.chiave === key)?.etichetta ?? key ?? '';
  const list = q.data ?? [];
  if (!list.length) return null;
  return (
    <div className="mt-3 space-y-2 text-xs">
      <h3 className="text-sm font-semibold">Che cosa diventano le righe</h3>
      <p className="text-ink/60">
        Ogni lettura del programma fa nascere o aggiorna le cose dell’azienda che le sue righe nominano, con il programma come fonte. Il sistema decide da un campione; tu puoi correggere.
      </p>
      {list.map((m) => {
        const map = m.mappatura && 'riga' in m.mappatura ? m.mappatura : null;
        const change = (patch: Partial<ProgramMappingDto>) => map && save.mutate({ lettura: m.lettura, mappatura: { ...map, ...patch } });
        return (
          <div key={m.lettura} className="rounded border border-ink/10 px-3 py-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{m.etichetta}</span>
              {m.fonte === 'persona' && <Badge>scelta da te</Badge>}
              {m.esito && (
                <span className="text-ink/55">
                  {m.esito.righe} righe · {m.esito.voci} voci{m.esito.senza_tipo ? ` · ${m.esito.senza_tipo} senza un tipo` : ''}
                  {m.applicata_il ? ` · ${dateTime(m.applicata_il)}` : ''}
                </span>
              )}
              <span className="flex-1" />
              <Button variant="ghost" disabled={redo.isPending} onClick={() => redo.mutate(m.lettura)}>
                {redo.isPending && redo.variables === m.lettura ? 'Rifaccio…' : 'Rifai dal campione'}
              </Button>
            </div>
            {!map ? (
              <p className="mt-1 text-ink/55">Nessuna mappatura ancora: si fa alla prossima sincronizzazione, o con «Rifai dal campione».</p>
            ) : (
              <div className="mt-1 space-y-1">
                <label className="flex flex-wrap items-center gap-2">
                  <span className="text-ink/60">Ogni riga è</span>
                  <Select
                    value={map.riga.cosa === 'voce' ? (map.riga.tipo ?? '') : map.riga.cosa === 'senza_tipo' ? '__senza' : ''}
                    disabled={save.isPending}
                    onChange={(e) => {
                      const v = e.target.value;
                      change({
                        riga:
                          v === '' ? { ...map.riga, cosa: 'niente', tipo: null } : v === '__senza' ? { ...map.riga, cosa: 'senza_tipo', tipo: null } : { ...map.riga, cosa: 'voce', tipo: v, attributi: map.riga.tipo === v ? map.riga.attributi : [] },
                      });
                    }}
                  >
                    <option value="">niente (righe tecniche)</option>
                    {map.riga.senza_tipo && <option value="__senza">{map.riga.senza_tipo.cosa} (senza un tipo)</option>}
                    {types.map((x) => (
                      <option key={x.chiave} value={x.chiave}>
                        {x.etichetta}
                      </option>
                    ))}
                  </Select>
                  {map.riga.cosa === 'voce' && map.riga.nome && <span className="text-ink/55">nome dalla colonna «{map.riga.nome}»</span>}
                </label>
                {map.cose.map((c, i) => (
                  <div key={`${c.tipo}/${c.nome}`} className="flex flex-wrap items-center gap-2">
                    <span className="text-ink/60">nomina</span>
                    <span>
                      {label(c.tipo)} dalla colonna «{c.nome}»{c.attributi.length ? ` (${c.attributi.map((a) => `${a.attributo} da «${a.colonna}»`).join(', ')})` : ''}
                    </span>
                    <button className="text-accent hover:underline" disabled={save.isPending} onClick={() => change({ cose: map.cose.filter((_, j) => j !== i) })}>
                      togli
                    </button>
                  </div>
                ))}
                {map.perche && <p className="text-ink/50">{map.perche}</p>}
              </div>
            )}
            {m.esito?.errore && <p className="mt-1 text-red-700">{m.esito.errore}</p>}
          </div>
        );
      })}
      <ErrorText>{save.error ? errorMessage(save.error) : redo.error ? errorMessage(redo.error) : ''}</ErrorText>
    </div>
  );
}
